import React from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Chip,
    Box,
    IconButton,
} from '@mui/material';
import {
    Star,
    StarBorder,
    PriceCheck,
    Inventory,
    Cake,
    Summarize,
    Payment,
    Analytics,
    Groups,
    Calculate,
    LocalOffer,
    LocalShipping,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Tool } from '../../../types/tools';

interface ToolCardProps {
    tool: Tool;
    isFavorite: boolean;
    onToggleFavorite: (toolId: string) => void;
    onLaunch: (toolId: string) => void;
}

const iconMap: Record<string, React.ElementType> = {
    PriceCheck,
    Inventory,
    Cake,
    Summarize,
    Payment,
    Analytics,
    Groups,
    Calculate,
    LocalOffer,
    LocalShipping,
};

const categoryColors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
    customer_service: 'primary',
    inventory: 'warning',
    reports: 'info',
    analytics: 'success',
    utilities: 'secondary',
};

const statusColors: Record<string, 'success' | 'warning' | 'default'> = {
    active: 'success',
    beta: 'warning',
    coming_soon: 'default',
};

export const ToolCard: React.FC<ToolCardProps> = ({
    tool,
    isFavorite,
    onToggleFavorite,
    onLaunch,
}) => {
    const navigate = useNavigate();
    const IconComponent = iconMap[tool.icon] || PriceCheck;

    const handleLaunch = () => {
        if (tool.status === 'active') {
            onLaunch(tool.id);
            navigate(tool.path);
        }
    };

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: tool.status === 'active' ? 'translateY(-4px)' : 'none',
                    boxShadow: tool.status === 'active' ? 6 : 1,
                },
                opacity: tool.status === 'coming_soon' ? 0.7 : 1,
            }}
        >
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box
                        sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            borderRadius: 2,
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <IconComponent sx={{ fontSize: 32 }} />
                    </Box>
                    <IconButton
                        size="small"
                        onClick={() => onToggleFavorite(tool.id)}
                        sx={{ color: isFavorite ? 'warning.main' : 'action.disabled' }}
                    >
                        {isFavorite ? <Star /> : <StarBorder />}
                    </IconButton>
                </Box>

                <Typography variant="h6" component="h2" gutterBottom>
                    {tool.name}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {tool.description}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        label={tool.category.replace('_', ' ')}
                        size="small"
                        color={categoryColors[tool.category]}
                        variant="outlined"
                    />
                    <Chip
                        label={tool.status.replace('_', ' ')}
                        size="small"
                        color={statusColors[tool.status]}
                    />
                </Box>

                {tool.usageCount !== undefined && tool.usageCount > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Used {tool.usageCount} {tool.usageCount === 1 ? 'time' : 'times'}
                    </Typography>
                )}
            </CardContent>

            <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                    fullWidth
                    variant={tool.status === 'active' ? 'contained' : 'outlined'}
                    disabled={tool.status !== 'active'}
                    onClick={handleLaunch}
                >
                    {tool.status === 'active' ? 'Launch Tool' : 'Coming Soon'}
                </Button>
            </CardActions>
        </Card>
    );
};
